import { getLaunchCredential, captureTerminalLaunchBinding, getTerminalLaunchBinding } from '../utils/launchCredentials';
import { sessionStateManager } from '../utils/SessionStateManager';
import { detectEnvironmentFromHostname, getBrandUrls, detectBrandFromHostname } from '../utils/brand-urls';
import { getDevBrandSlug } from '../contexts/DevBrandContext';

export interface TerminalReturn {
  status: 'verified' | 'failed' | 'cancelled';
  targetOrigin: string;
  action: string;
  message: { type: string; sessionId: string; status: string; timestamp: number; externalUserId?: string };
}
export const TERMINAL_RETURN_EVENT = 'verification:terminal-return';
const inFlight = new Map<string, Promise<TerminalReturn | null>>();
const automaticAttempts = new Map<string, number>();

export function terminalStatusUrl(sessionId: string): URL {
  const environment = detectEnvironmentFromHostname(window.location.hostname);
  const { apiUrl } = getBrandUrls(environment);
  return new URL(`/api/v1/sessions/${encodeURIComponent(sessionId)}/status`, apiUrl);
}

/** Two automatic reads per page lifetime, not a poll loop. Deliberate actions
 * always re-read. Browser notifications still require merchant validation. */
export function recoverTerminalReturn(sessionId?: string, deliberate = false): Promise<TerminalReturn | null> {
  const binding = getTerminalLaunchBinding();
  const id = sessionId || new URLSearchParams(window.location.search).get('sessionId') || binding?.sessionId || sessionStateManager.getState()?.sessionId;
  if (!id) return Promise.resolve(null);
  const existing = inFlight.get(id);
  if (existing) return existing;
  const attempts = automaticAttempts.get(id) || 0;
  if (!deliberate && attempts >= 2) return Promise.resolve(null);
  if (!deliberate) automaticAttempts.set(id, attempts + 1);
  const request = (async () => {
    const stored = sessionStateManager.getState();
    const bound = binding?.sessionId === id ? binding.params : null;
    const token = getLaunchCredential('sessionToken') || bound?.sessionToken || (stored?.sessionId === id ? stored.config?.sessionToken : null);
    const handoff = getLaunchCredential('handoffToken') || getLaunchCredential('token') || bound?.handoffToken || bound?.token;
    if (!token && !handoff) return null;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5_000);
    try {
      const headers: Record<string, string> = { Authorization: token ? `Bearer ${token}` : `Handoff ${handoff}` };
      const devBrand = getDevBrandSlug();
      if (devBrand) headers['X-Brand-Override'] = devBrand;
      const response = await fetch(terminalStatusUrl(id), { headers, signal: controller.signal, cache: 'no-store' });
      if (!response.ok) return null;
      const result = (await response.json()).terminalReturn as TerminalReturn | undefined;
      const brand = detectBrandFromHostname(window.location.hostname) || devBrand;
      if (!result || !['verified', 'failed', 'cancelled'].includes(result.status) ||
          result.message?.sessionId !== id || result.message.status !== result.status ||
          result.message.type !== `${brand}:verification:complete` ||
          !/^[A-Za-z0-9_-]{40,2048}$/.test(result.action)) return null;
      const origin = new URL(result.targetOrigin);
      if (origin.origin !== result.targetOrigin || !['https:', 'http:'].includes(origin.protocol)) return null;
      captureTerminalLaunchBinding(id);
      window.dispatchEvent(new CustomEvent(TERMINAL_RETURN_EVENT, { detail: result }));
      return result;
    } catch { return null; } finally { clearTimeout(timer); }
  })().finally(() => inFlight.delete(id));
  inFlight.set(id, request);
  return request;
}

export function returnToMerchant(result: TerminalReturn): void {
  const url = terminalStatusUrl(result.message.sessionId);
  url.searchParams.set('return', result.action);
  window.location.assign(url.toString());
}
